/**
 * Edit mode: fetch the saved password of a connection that has one (moved out of `NewConnectionPopup`, C0).
 *
 * Round B R2: a late answer never overwrites a password the user already typed, and it is not an edit
 * (`dontUpdateMeta`): `passwordEdited` stays false and a result tested with the saved password stays on screen. C0: it
 * lands in the draft of the engine the password belongs to — the live form while it shows that engine, else that
 * engine's cached draft (`useEngineDrafts.hydratePassword`).
 */
import { useEffect } from 'react';
import type { Connection } from '@/types/electron';
import type { DatabaseEngine } from '../../components/connectionFormSchema';
import { getDecryptedPassword } from '../../services/connectionService';

export function useStoredPasswordHydration(
  editConnection: Connection | undefined,
  hydrate: (engine: DatabaseEngine, password: string) => void,
): void {
  useEffect(() => {
    if (!editConnection?.hasStoredPassword || editConnection.password) return;
    let cancelled = false;
    void (async () => {
      try {
        const decrypted = await getDecryptedPassword(editConnection.id);
        if (cancelled || !decrypted) return;
        hydrate((editConnection.engine ?? 'mssql') as DatabaseEngine, decrypted);
      } catch (error) {
        console.warn('Failed to fetch stored password:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `hydrate` reads the live form; the fetch keys on the connection
  }, [editConnection]);
}
